<script setup lang="ts">
import QRCode from 'qrcode'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

import AppIcon from '../components/AppIcon.vue'
import PageBanner from '../components/PageBanner.vue'
import SiteFooter from '../components/SiteFooter.vue'
import SiteHeader from '../components/SiteHeader.vue'
import { ApiError, apiRequest } from '../services/api'

interface LobbyState {
  session: { id: string; join_code: string; name: string; phase: string; joining_locked: number; state_revision: number; answer_close_at: string | null }
  teams: Array<{ id: string; name: string; member_count: number; captain_player_id: string | null; active: number }>
  players: Array<{ id: string; display_name: string; team_id: string | null; active: number }>
  joinRequests: Array<{ id: string; display_name: string }>
  question: { id: string; prompt: string; host_notes: string | null } | null
  progress: { answered: number; eligible: number }
  groups: Array<{ id: string; canonical_answer: string; final_count: number; raw_answers: string; rank: number | null }>
  groupMembers: Array<{ id: string; group_id: string; raw_answer: string }>
  standings: Array<{ id: string; name: string; score: number }>
  standardAnswers?: Array<{ answer: string; count: number }>
  reveal?: Array<{ answer: string; count: number; rank: number; current: boolean }> | null
}

const sessionId = String(useRoute().params.sessionId)
const lobby = ref<LobbyState | null>(null)
const qrCode = ref('')
const error = ref('')
const connected = ref(false)
const selectedGroup = ref('')
const groupLabels = ref<Record<string, string>>({})
const destinationByPlayer = ref<Record<string, string>>({})
const pointsByTeam = ref<Record<string, number>>({})
const reasonByTeam = ref<Record<string, string>>({})
let socket: WebSocket | null = null
let reconnectTimer: number | undefined
let stopped = false

const phaseLabel = computed(() => (lobby.value?.session.phase ?? '').replaceAll('_', ' ').toLowerCase())
const activeTeams = computed(() => lobby.value?.teams.filter((team) => team.active) ?? [])
const activePlayers = computed(() => lobby.value?.players.filter((player) => player.active) ?? [])
const progressPercent = computed(() => {
  const progress = lobby.value?.progress
  return progress && progress.eligible ? Math.round((progress.answered / progress.eligible) * 100) : 0
})

async function refresh(): Promise<void> {
  try {
    lobby.value = await apiRequest<LobbyState>(`/api/admin/sessions/${sessionId}`)
    for (const group of lobby.value.groups) groupLabels.value[group.id] ??= group.canonical_answer
    qrCode.value = await QRCode.toDataURL(`${location.origin}/join/${lobby.value.session.join_code}`, { width: 360, margin: 1 })
  } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'Unable to load the host lobby.'
  }
}

function connect(): void {
  if (stopped) return
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
  socket = new WebSocket(`${protocol}//${location.host}/api/admin/sessions/${sessionId}/socket`, ['crowd-control'])
  socket.addEventListener('open', () => { connected.value = true })
  socket.addEventListener('message', () => { void refresh() })
  socket.addEventListener('close', () => {
    connected.value = false
    if (!stopped) reconnectTimer = window.setTimeout(connect, 1500)
  })
}

async function setJoining(locked: boolean): Promise<void> {
  await apiRequest(`/api/admin/sessions/${sessionId}/joining`, { method: 'POST', body: JSON.stringify({ locked }) })
  await refresh()
}

async function hostAction(action: string, confirmation?: string): Promise<void> {
  if (confirmation && !window.confirm(confirmation)) return
  error.value = ''
  try {
    await apiRequest(`/api/admin/sessions/${sessionId}/action`, { method: 'POST', body: JSON.stringify({ action }) })
    await refresh()
  } catch (caught) { error.value = caught instanceof ApiError ? caught.message : 'The host action failed.' }
}

async function mergeInto(targetGroupId: string): Promise<void> {
  if (!selectedGroup.value || selectedGroup.value === targetGroupId) return
  if (await mutateGroup({ sourceGroupId: selectedGroup.value, targetGroupId })) selectedGroup.value = ''
}

async function renameGroup(groupId: string): Promise<void> {
  await mutateGroup({ action: 'RENAME', groupId, canonicalAnswer: groupLabels.value[groupId] })
}

async function unmerge(groupId: string, memberId: string): Promise<void> {
  await mutateGroup({ action: 'UNMERGE', groupId, memberId })
}

async function mutateGroup(body: Record<string, unknown>): Promise<boolean> {
  error.value = ''
  try {
    await apiRequest(`/api/admin/sessions/${sessionId}/groups`, { method: 'POST', body: JSON.stringify(body) })
    await refresh()
    return true
  } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'The answer group could not be changed.'
    return false
  }
}

async function administer(body: Record<string, unknown>, confirmation?: string): Promise<void> {
  if (confirmation && !window.confirm(confirmation)) return
  error.value = ''
  try {
    await apiRequest(`/api/admin/sessions/${sessionId}/administration`, { method: 'POST', body: JSON.stringify(body) })
    await refresh()
  } catch (caught) { error.value = caught instanceof ApiError ? caught.message : 'The administration action failed.' }
}

function initials(name: string): string {
  return name.split(/\s+/).map((part) => part[0] ?? '').join('').slice(0, 2)
}

onMounted(async () => { await refresh(); connect() })
onBeforeUnmount(() => {
  stopped = true
  if (reconnectTimer) window.clearTimeout(reconnectTimer)
  socket?.close(1000, 'Leaving host')
})
</script>

<template>
  <div class="page-wrapper">
    <SiteHeader>
      <template #cta>
        <RouterLink
          to="/admin"
          class="btn--ghost"
        >
          <AppIcon name="settings" /><span class="btn-label">Builder</span>
        </RouterLink>
        <a
          :href="`/screen/${sessionId}`"
          target="_blank"
          rel="noopener"
          class="btn--secondary"
        >
          <AppIcon name="tv" /><span class="btn-label">Big Screen</span>
        </a>
      </template>
      <template #mobile-cta>
        <RouterLink
          to="/admin"
          class="btn--secondary"
        >
          Game builder
        </RouterLink>
        <a
          :href="`/screen/${sessionId}`"
          target="_blank"
          rel="noopener"
          class="btn--primary"
        >
          Open Big Screen
        </a>
      </template>
    </SiteHeader>
    <main>
      <PageBanner
        :title="lobby?.session.name ?? 'Host console'"
        crumb="Host console"
        kicker-strong="Live"
        :kicker="` ${phaseLabel || 'session'}`"
      >
        <span
          v-if="lobby"
          class="tag tag--primary"
        ><AppIcon
          name="hash"
          :size="16"
        /> {{ lobby.session.join_code }}</span>
        <span
          class="tag"
          :class="connected ? 'tag--primary' : 'tag--secondary'"
        >{{ connected ? 'Connected' : 'Reconnecting…' }}</span>
        <span
          v-if="lobby"
          class="tag"
        ><AppIcon
          name="users"
          :size="16"
        /> {{ activePlayers.length }} players · {{ activeTeams.length }} teams</span>
      </PageBanner>

      <div class="container section section--tight stack stack--lg">
        <p
          v-if="error"
          class="alert"
          role="alert"
        >
          {{ error }}
        </p>
        <template v-if="lobby">
          <!-- current question -->
          <section
            v-if="lobby.question"
            v-reveal
            class="panel panel--dark"
          >
            <span class="sub-title"><strong>Current</strong> question</span>
            <h2 class="text-xxl fw-6 mt-12">
              {{ lobby.question.prompt }}
            </h2>
            <p
              v-if="lobby.question.host_notes"
              class="notice mt-16"
            >
              {{ lobby.question.host_notes }}
            </p>
            <div
              v-if="lobby.standardAnswers?.length"
              class="mt-16"
            >
              <p class="text-sm fw-6 upper secondary-text">
                Standard answers
              </p>
              <ul class="row-flex mt-8">
                <li
                  v-for="entry in lobby.standardAnswers"
                  :key="entry.answer"
                  class="tag tag--primary"
                >
                  {{ entry.answer }} <span class="muted-text">× {{ entry.count }}</span>
                </li>
              </ul>
            </div>
            <div class="row-flex mt-16">
              <div
                class="progress-bar"
                style="flex: 1 1 12rem"
              >
                <div
                  class="progress-bar-percent"
                  :style="{ width: `${progressPercent}%` }"
                />
              </div>
              <p class="fw-6">
                {{ lobby.progress.answered }} of {{ lobby.progress.eligible }} eligible teams answered
              </p>
            </div>
          </section>

          <!-- controls -->
          <nav
            v-reveal
            class="panel host-controls"
            aria-label="Live game controls"
          >
            <button
              v-if="lobby.session.phase === 'LOBBY' || lobby.session.phase === 'STANDINGS'"
              type="button"
              class="btn--primary"
              @click="hostAction('OPEN_QUESTION')"
            >
              <AppIcon name="play" /> Present question
            </button>
            <button
              v-if="lobby.session.phase === 'QUESTION_OPEN'"
              type="button"
              class="btn--primary"
              @click="hostAction('CLOSE_QUESTION')"
            >
              <AppIcon name="lock" /> Close answers
            </button>
            <button
              v-if="lobby.session.phase === 'QUESTION_CLOSED'"
              type="button"
              class="btn--primary"
              @click="hostAction('START_REVIEW')"
            >
              <AppIcon name="eye" /> Review answers
            </button>
            <button
              v-if="lobby.session.phase === 'QUESTION_CLOSED' || lobby.session.phase === 'REVIEW'"
              type="button"
              class="btn--secondary"
              @click="hostAction('REOPEN_ANSWERS')"
            >
              <AppIcon name="lock-open" /> Reopen answers
            </button>
            <button
              v-if="lobby.session.phase === 'QUESTION_CLOSED' || lobby.session.phase === 'REVIEW'"
              type="button"
              class="btn--danger"
              @click="hostAction('RESET_ANSWERS', 'Reset every accepted answer for this question? This cannot be undone.')"
            >
              <AppIcon name="refresh" /> Reset question answers
            </button>
            <button
              v-if="['QUESTION_OPEN', 'QUESTION_CLOSED', 'REVIEW'].includes(lobby.session.phase)"
              type="button"
              class="btn--danger"
              @click="hostAction('SKIP_QUESTION', 'Skip this question without awarding points?')"
            >
              <AppIcon name="skip" /> Skip question
            </button>
            <button
              v-if="lobby.session.phase === 'REVIEW'"
              type="button"
              class="btn--primary"
              @click="hostAction('REVEAL')"
            >
              <AppIcon name="sparkles" /> Reveal and score
            </button>
            <button
              v-if="lobby.session.phase === 'REVEALED'"
              type="button"
              class="btn--primary"
              @click="hostAction('SHOW_STANDINGS')"
            >
              <AppIcon name="trophy" /> Show standings
            </button>
            <button
              v-if="lobby.session.phase === 'STANDINGS'"
              type="button"
              class="btn--secondary"
              @click="hostAction('COMPLETE')"
            >
              <AppIcon name="flag" /> Finish game
            </button>
            <button
              v-if="!['COMPLETE', 'ENDED'].includes(lobby.session.phase)"
              type="button"
              class="btn--danger"
              @click="hostAction('END', 'End this live session early? Players will no longer be able to continue.')"
            >
              <AppIcon name="x" /> End session early
            </button>
          </nav>

          <!-- review -->
          <section
            v-if="lobby.session.phase === 'REVIEW'"
            v-reveal
            class="panel panel--dark stack"
          >
            <div>
              <h2 class="text-xxl fw-6">
                Review answer groups
              </h2>
              <p class="muted-text">
                Select one group, then merge it into another. Rename a group to set the label the crowd will see.
              </p>
            </div>
            <article
              v-for="group in lobby.groups"
              :key="group.id"
              class="list-card group-card"
            >
              <label class="check-row"><input
                v-model="selectedGroup"
                type="radio"
                :value="group.id"
              > <strong>{{ group.canonical_answer }}</strong></label>
              <span class="muted-text">{{ group.final_count }} · {{ group.raw_answers }}</span>
              <div class="inline-form">
                <label
                  class="sr-only"
                  :for="`group-label-${group.id}`"
                >Canonical label</label>
                <div class="input-single input-single--soft">
                  <AppIcon name="pencil" />
                  <input
                    :id="`group-label-${group.id}`"
                    v-model="groupLabels[group.id]"
                    maxlength="120"
                  >
                </div>
                <button
                  type="button"
                  class="btn--secondary btn--sm"
                  @click="renameGroup(group.id)"
                >
                  Rename
                </button>
              </div>
              <div
                v-if="group.final_count > 1"
                class="member-row"
              >
                <button
                  v-for="member in lobby.groupMembers.filter((item) => item.group_id === group.id)"
                  :key="member.id"
                  class="btn--ghost btn--sm"
                  type="button"
                  @click="unmerge(group.id, member.id)"
                >
                  Separate “{{ member.raw_answer }}”
                </button>
              </div>
              <div v-if="selectedGroup && selectedGroup !== group.id">
                <button
                  type="button"
                  class="btn--primary btn--sm"
                  @click="mergeInto(group.id)"
                >
                  Merge selected here
                </button>
              </div>
            </article>
          </section>

          <!-- reveal -->
          <section
            v-if="lobby.reveal?.length && ['REVEALED', 'STANDINGS'].includes(lobby.session.phase)"
            v-reveal
            class="panel panel--dark"
          >
            <h2 class="text-xxl fw-6">
              The crowd's top answers
            </h2>
            <ol class="standings-list mt-16">
              <li
                v-for="entry in lobby.reveal"
                :key="`${entry.rank}-${entry.answer}`"
              >
                <strong><span
                  class="avatar"
                  :class="{ 'avatar--secondary': entry.rank === 1 }"
                >{{ entry.rank }}</span>{{ entry.answer }}<span
                  v-if="!entry.current"
                  class="tag tag--muted"
                >from past plays</span></strong><span>{{ entry.count }} {{ entry.count === 1 ? 'answer' : 'answers' }}</span>
              </li>
            </ol>
          </section>

          <!-- standings -->
          <section
            v-if="['REVEALED', 'STANDINGS', 'COMPLETE'].includes(lobby.session.phase)"
            v-reveal
            class="panel panel--dark"
          >
            <h2 class="text-xxl fw-6">
              Standings
            </h2>
            <ol class="standings-list mt-16">
              <li
                v-for="(team, index) in lobby.standings"
                :key="team.id"
              >
                <strong><span
                  class="avatar"
                  :class="{ 'avatar--secondary': index === 0 }"
                >{{ index + 1 }}</span>{{ index + 1 }}. {{ team.name }}</strong><span>{{ team.score }} points</span>
              </li>
            </ol>
          </section>

          <div class="grid grid--sidebar">
            <!-- join code -->
            <section
              v-reveal
              class="panel panel--dark lobby-code"
            >
              <h2 class="text-xl fw-6">
                Game ID
              </h2>
              <strong>{{ lobby.session.join_code }}</strong>
              <img
                v-if="qrCode"
                :src="qrCode"
                :alt="`QR code to join game ${lobby.session.join_code}`"
              >
              <button
                type="button"
                :class="lobby.session.joining_locked ? 'btn--secondary' : 'btn--danger'"
                @click="setJoining(!lobby.session.joining_locked)"
              >
                <AppIcon :name="lobby.session.joining_locked ? 'lock-open' : 'lock'" />
                {{ lobby.session.joining_locked ? 'Unlock new players' : 'Lock new players' }}
              </button>
            </section>

            <!-- teams and players -->
            <section
              v-reveal
              class="panel panel--dark stack stack--lg"
            >
              <h2 class="text-xxl fw-6">
                {{ lobby.players.length }} players · {{ lobby.teams.length }} teams
              </h2>
              <p
                v-if="!activeTeams.length"
                class="muted-text"
              >
                No teams yet. Players create or join teams from their phones.
              </p>
              <article
                v-for="team in activeTeams"
                :key="team.id"
                class="list-card"
              >
                <div class="row-flex row-flex--between">
                  <strong class="row-flex"><span class="avatar avatar--secondary">{{ initials(team.name) }}</span>{{ team.name }}</strong>
                  <span class="tag"><AppIcon
                    name="users"
                    :size="16"
                  /> {{ team.member_count }} players</span>
                </div>
                <div class="inline-form">
                  <div class="input-single input-single--soft">
                    <AppIcon name="medal" />
                    <input
                      v-model.number="pointsByTeam[team.id]"
                      type="number"
                      min="-10000"
                      max="10000"
                      aria-label="Point adjustment"
                      placeholder="Points"
                    >
                  </div>
                  <div class="input-single input-single--soft">
                    <AppIcon name="pencil" />
                    <input
                      v-model="reasonByTeam[team.id]"
                      aria-label="Adjustment reason"
                      placeholder="Reason"
                    >
                  </div>
                  <button
                    type="button"
                    class="btn--secondary btn--sm"
                    :disabled="!pointsByTeam[team.id] || !reasonByTeam[team.id]?.trim()"
                    @click="administer({ action: 'ADJUST_SCORE', teamId: team.id, points: pointsByTeam[team.id], reason: reasonByTeam[team.id] })"
                  >
                    Adjust score
                  </button>
                </div>
                <div class="compact-actions">
                  <button
                    class="btn--danger btn--sm"
                    type="button"
                    @click="administer({ action: 'DEACTIVATE_TEAM', teamId: team.id }, `Deactivate ${team.name}?`)"
                  >
                    Deactivate team
                  </button>
                </div>
              </article>

              <h3 class="text-xl fw-6">
                Player administration
              </h3>
              <p
                v-if="!activePlayers.length"
                class="muted-text"
              >
                Nobody has joined yet. Share the Game ID or the QR code.
              </p>
              <article
                v-for="player in activePlayers"
                :key="player.id"
                class="list-card"
              >
                <div class="row-flex row-flex--between">
                  <strong class="row-flex"><span class="avatar">{{ initials(player.display_name) }}</span>{{ player.display_name }}</strong>
                  <span class="tag tag--muted">{{ lobby.teams.find((team) => team.id === player.team_id)?.name ?? 'Choosing a team' }}</span>
                </div>
                <div class="inline-form">
                  <div class="input-single input-single--soft input-single--select">
                    <select
                      v-model="destinationByPlayer[player.id]"
                      :aria-label="`Move ${player.display_name} to team`"
                    >
                      <option value="">
                        Move to…
                      </option>
                      <option
                        v-for="team in lobby.teams.filter((item) => item.active && item.id !== player.team_id)"
                        :key="team.id"
                        :value="team.id"
                      >
                        {{ team.name }}
                      </option>
                    </select>
                  </div>
                  <button
                    type="button"
                    class="btn--secondary btn--sm"
                    :disabled="!destinationByPlayer[player.id]"
                    @click="administer({ action: 'MOVE_PLAYER', playerId: player.id, teamId: destinationByPlayer[player.id] })"
                  >
                    Move
                  </button>
                  <button
                    v-if="player.team_id && lobby.teams.find((team) => team.id === player.team_id)?.captain_player_id !== player.id"
                    type="button"
                    class="btn--ghost btn--sm"
                    @click="administer({ action: 'TRANSFER_CAPTAIN', playerId: player.id, teamId: player.team_id })"
                  >
                    <AppIcon
                      name="crown"
                      :size="18"
                    /> Make captain
                  </button>
                  <button
                    class="btn--danger btn--sm"
                    type="button"
                    @click="administer({ action: 'REMOVE_PLAYER', playerId: player.id }, `Remove ${player.display_name} from this session?`)"
                  >
                    Remove
                  </button>
                </div>
              </article>
            </section>
          </div>
        </template>
      </div>
    </main>
    <SiteFooter />
  </div>
</template>
